import { getLogger, setLogger } from './js/utils/logger-context';

// Import SCSS
import './scss/main.scss';

// Import all component initializations dynamically
import * as accordion from './js/init-tako-accordion';
import * as alert from './js/init-tako-alert';
import * as breadcrumbs from './js/init-tako-breadcrumbs';
import * as button from './js/init-tako-button';
import * as card from './js/init-tako-card';
import * as checkbox from './js/init-tako-checkbox';
import * as dropdown from './js/init-tako-dropdown';
import * as list from './js/init-tako-list';
import * as link from './js/init-tako-link';
import * as modal from './js/init-tako-modal';
import * as pagination from './js/init-tako-pagination';
import * as popover from './js/init-tako-popover';
import * as radioButtonGroup from './js/init-tako-radio-button-group';
import * as select from './js/init-tako-select';
import * as table from './js/init-tako-table';
import * as tag from './js/init-tako-tag';
import * as textInput from './js/init-tako-text-input';
import * as toggleSwitch from './js/init-tako-toggle-switch';
import * as tooltip from './js/init-tako-tooltip';
import { type UnresolvedComponentScope } from './js/utils/resolveComponentScope';
import { getContrastColor } from './js/utils/getContrastColor';
import { type Logger } from './types/logger';
import { getErrorMessage } from './js/utils/getErrorMessage';
import { theme } from './js/theme/theme';
import {
    chartjsColors,
    chartjsFont,
    chartjsOptions,
    chartjsPlugins,
} from './js/charts/chartjs/chart-defaults';

type InitPromise = Promise<{ name: string; error?: unknown }>;

type ComponentName =
    | 'accordion'
    | 'alert'
    | 'breadcrumbs'
    | 'button'
    | 'card'
    | 'checkbox'
    | 'dropdown'
    | 'list'
    | 'link'
    | 'modal'
    | 'pagination'
    | 'popover'
    | 'radioButtonGroup'
    | 'select'
    | 'table'
    | 'tag'
    | 'textInput'
    | 'toggleSwitch'
    | 'tooltip';
type ComponentMethods = {
    init: (unresolvedScope: UnresolvedComponentScope) => void;
    toggle?: (selector: string, open: boolean | null) => void;
};

// Centralized component registry
const components: Record<ComponentName, ComponentMethods> = {
    accordion,
    alert,
    breadcrumbs,
    button,
    card,
    checkbox,
    dropdown,
    list,
    link,
    /*@ts-expect-error modal's toggle function does not match ComponentMethods['toggle'] type */
    modal,
    pagination,
    popover,
    radioButtonGroup,
    select,
    table,
    tag,
    textInput,
    toggleSwitch,
    tooltip,
};

const safelyInitComponent = (
    name: ComponentName,
    initFn: (unresolvedScope: UnresolvedComponentScope) => void,
    unresolvedScope: UnresolvedComponentScope,
    logger: Logger,
): InitPromise => {
    return new Promise((resolve) => {
        try {
            initFn(unresolvedScope);
            resolve({ name });
        } catch (error) {
            logger?.error(
                `Failed to initialize Tako ${name}: ${getErrorMessage(error)}`,
                { error },
            );
            resolve({ name, error });
        }
    });
};

// Initialize all Tako components
export const initAllTakoComponents = async (
    unresolvedScope: UnresolvedComponentScope,
    externalLogger: Logger,
) => {
    setLogger(externalLogger);
    const logger = getLogger();

    const initPromises: InitPromise[] = Object.entries(components).reduce(
        (acc, [key, { init }]) => {
            // ToDo: there's an ongoing issue with pagination component: https://app.asana.com/0/1200521765515341/1209536215520354
            if (key !== 'pagination') {
                acc.push(
                    safelyInitComponent(
                        key as ComponentName,
                        init,
                        unresolvedScope,
                        logger,
                    ),
                );
            }
            return acc;
        },
        [] as InitPromise[],
    );
    const results = await Promise.all(initPromises);
    const failedComponents = results.filter(({ error }) => error);

    if (failedComponents.length > 0) {
        logger.warn(`Tako: Some components failed to initialize:`, {
            failedComponents,
        });
    }

    return results;
};

// Default export with all components
export default {
    initAllTakoComponents,
    ...components,
    theme,
    chartjsColors,
    chartjsFont,
    chartjsOptions,
    chartjsPlugins,
    getContrastColor,
};

// Named exports for individual methods
export {
    accordion,
    alert,
    breadcrumbs,
    button,
    card,
    checkbox,
    dropdown,
    list,
    link,
    modal,
    pagination,
    popover,
    radioButtonGroup,
    select,
    table,
    tag,
    textInput,
    toggleSwitch,
    tooltip,
    theme,
    chartjsColors,
    chartjsFont,
    chartjsOptions,
    chartjsPlugins,
    getContrastColor,
};
